import { useCallback, useEffect, useRef, useState } from 'react'
import { getAuth } from 'firebase/auth'

// A viewer sitting on an offline page should see it flip to LIVE without a reload.
export const LIVE_POLL_INTERVAL_MS = 15000

const buildUrl = (username) => `/api/live/creators/${encodeURIComponent(username)}`

const INITIAL_STATE = {
  stream: null,
  isLoading: true,
  error: null,
  notFound: false,
  unauthorized: false,
  canWatch: false
}

/**
 * Reads the viewer-facing live stream for a creator.
 *
 * GET /api/live/creators/<username> with `Authorization: Bearer <firebase id token>`.
 * Per the API contract: 200 `{ stream: PublicStream | null }`, 401 when not signed in,
 * 404 when there is no such creator, the flag is off, or the stream is not published.
 *
 * The request is skipped entirely while `username` is undefined (the Next.js router
 * query is empty on the first render) or while `enabled` is false (auth still resolving,
 * or the viewer is signed out) — in that case the hook stays in its initial loading
 * state and the caller decides what to render.
 */
export const useCreatorLiveStream = (username, options = {}) => {
  const { enabled = true, pollIntervalMs = LIVE_POLL_INTERVAL_MS } = options

  const [state, setState] = useState(INITIAL_STATE)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Never write state into an unmounted component (polling outlives navigation).
  const commit = useCallback((next) => {
    if (isMountedRef.current) setState(next)
  }, [])

  const readIdToken = useCallback(async () => {
    try {
      const currentUser = getAuth().currentUser
      return currentUser ? await currentUser.getIdToken() : null
    } catch (e) {
      console.error('useCreatorLiveStream: could not read the Firebase id token', e)
      return null
    }
  }, [])

  const fetchStream = useCallback(async () => {
    if (!username || !enabled) return

    // Signed-out viewers still fetch: the API answers with a gated projection so
    // the page can show a "this creator is live" prompt instead of nothing.
    const idToken = await readIdToken()

    try {
      const response = await fetch(buildUrl(username), {
        method: 'GET',
        headers: idToken ? { Authorization: `Bearer ${idToken}` } : {}
      })

      if (response.status === 401) {
        commit({ ...INITIAL_STATE, isLoading: false, unauthorized: true })
        return
      }

      if (response.status === 404) {
        commit({ ...INITIAL_STATE, isLoading: false, notFound: true })
        return
      }

      if (!response.ok) {
        const body = await response.json().catch(() => null)
        throw new Error(body?.error || `Live stream request failed (${response.status})`)
      }

      const body = await response.json()
      const stream = body?.stream || null
      const canWatch = body?.canWatch === true

      // A 200 with a null stream means the same thing to a viewer as a 404.
      // `unauthorized` now means "cannot watch", not "cannot see" — the stream
      // is still handed over so the gate can name the creator and their status.
      commit({
        ...INITIAL_STATE,
        isLoading: false,
        stream,
        canWatch,
        notFound: !stream,
        unauthorized: Boolean(stream) && !canWatch
      })
    } catch (e) {
      console.error('useCreatorLiveStream: failed to load the live stream', e)
      commit({ ...INITIAL_STATE, isLoading: false, error: e })
    }
  }, [username, enabled, readIdToken, commit])

  // Initial load. Deliberately separate from the polling effect below so that a
  // change in poll state never re-triggers a fetch.
  useEffect(() => {
    // A disabled or target-less hook is idle, NOT loading. Committing
    // INITIAL_STATE here left isLoading true forever and stranded the caller on
    // a spinner.
    if (!username || !enabled) {
      commit({ ...INITIAL_STATE, isLoading: false })
      return
    }

    commit(INITIAL_STATE)
    fetchStream()
  }, [username, enabled, fetchStream, commit])

  // A 404 means this creator has no stream at all — the feature is off for them,
  // or they have never set one up. That will not change while the page is open,
  // and this hook runs on every public creator profile, so polling stops rather
  // than 404ing every interval forever.
  //
  // A stream that exists but is idle DOES keep polling: that is the case where
  // the page is expected to come alive on its own.
  const shouldPoll = Boolean(username) && enabled && Boolean(pollIntervalMs) && !state.notFound

  useEffect(() => {
    if (!shouldPoll) return undefined

    const intervalId = setInterval(fetchStream, pollIntervalMs)
    return () => clearInterval(intervalId)
  }, [shouldPoll, pollIntervalMs, fetchStream])

  return { ...state, refetch: fetchStream }
}

export default useCreatorLiveStream
